/**
 * audit-log-tabs.tsx — Cài đặt → Nhật ký kiểm toán (mẫu SaleSmartly):
 *   - Nhật ký thao tác: ai làm gì, ở mô-đun nào, lên đối tượng nào.
 *   - Nhật ký đăng nhập: thành công/thất bại, IP (cảnh báo khi đổi IP), thiết bị.
 *   - Nhật ký trạng thái trực tuyến: ai chuyển Trực tuyến/Bận/Ngoại tuyến, bằng
 *     cách nào (đăng nhập, tự chuyển, mất kết nối…) và giữ trong bao lâu.
 * Chỉ owner/admin thấy các mục này (settings-page khai báo roles).
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, CircleDot, Download, History, LogIn } from 'lucide-react'
import { i18n } from '@/lib/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Pagination } from '@/components/shared/pagination'
import { ErrorState } from '@/components/shared/feedback'
import { useTeam } from '@/hooks/use-settings'
import {
  downloadAuditCsv, useActivityLogs, useAuditModules, useLoginLogs,
  type ActivityItem, type AuditExportKind, type LoginItem,
} from '@/hooks/use-audit'
import { apiError } from '@/lib/api-client'
import { usePresenceLogs, type PresenceLogItem } from '@/hooks/use-presence'
import { PRESENCE_DOT, PRESENCE_OPTIONS } from '@/lib/presence'
import { cn } from '@/lib/utils'
import {
  FAILURE_LABELS, IP_CHANGED_WARNING, deviceText, formatTime, quickRange, type AuditFilters,
} from './audit-lib'

const PAGE_SIZE = 20

// ── Thanh lọc dùng chung ─────────────────────────────────────────────

function FilterBar({
  filters, onChange, onExport, exporting, children,
}: {
  filters: AuditFilters
  onChange: (patch: Partial<AuditFilters>) => void
  onExport: () => void
  exporting: boolean
  children?: React.ReactNode
}) {
  const { data: members } = useTeam()
  const { t } = useTranslation('settings')
  const quick = (d: 1 | 7 | 30) => onChange(quickRange(d))
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-1">
        <Input
          type="date"
          value={filters.from ?? ''}
          max={filters.to || undefined}
          onChange={(e) => onChange({ from: e.target.value || undefined })}
          className="h-8 w-[140px]"
          aria-label={t('settings:audit.filters.fromDate')}
        />
        <span className="text-muted-foreground">–</span>
        <Input
          type="date"
          value={filters.to ?? ''}
          min={filters.from || undefined}
          onChange={(e) => onChange({ to: e.target.value || undefined })}
          className="h-8 w-[140px]"
          aria-label={t('settings:audit.filters.toDate')}
        />
      </div>
      <div className="flex gap-1">
        <Button size="sm" variant="ghost" onClick={() => quick(1)}>{t('settings:audit.filters.today')}</Button>
        <Button size="sm" variant="ghost" onClick={() => quick(7)}>{t('settings:audit.filters.days7')}</Button>
        <Button size="sm" variant="ghost" onClick={() => quick(30)}>{t('settings:audit.filters.days30')}</Button>
      </div>
      <Select value={filters.userId ?? 'all'} onValueChange={(v) => onChange({ userId: v })}>
        <SelectTrigger className="w-[180px]" aria-label={t('settings:audit.filters.member')}>
          <SelectValue placeholder={t('settings:audit.filters.allMembers')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('settings:audit.filters.allMembers')}</SelectItem>
          {(members ?? []).map((m) => (
            <SelectItem key={m.id} value={m.id}>{m.fullName}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {children}
      <div className="ml-auto flex gap-2">
        {(filters.from || filters.to || (filters.userId && filters.userId !== 'all')
          || (filters.module && filters.module !== 'all') || filters.success
          || (filters.status && filters.status !== 'all')) && (
          <Button size="sm" variant="ghost" onClick={() => onChange({
            from: undefined, to: undefined, userId: 'all', module: 'all', success: '', status: 'all',
          })}>
            {t('settings:audit.filters.clear')}
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={onExport} disabled={exporting}>
          <Download className="h-4 w-4" /> {t('settings:audit.filters.export')}
        </Button>
      </div>
    </div>
  )
}

function useFilters() {
  const [filters, setFilters] = useState<AuditFilters>({ page: 1, limit: PAGE_SIZE })
  const [exporting, setExporting] = useState(false)
  // Đổi điều kiện lọc → quay về trang 1.
  const change = (patch: Partial<AuditFilters>) => setFilters((f) => ({ ...f, ...patch, page: 1 }))
  const setPage = (page: number) => setFilters((f) => ({ ...f, page }))
  const exportCsv = async (kind: AuditExportKind) => {
    setExporting(true)
    try {
      await downloadAuditCsv(kind, filters)
    } catch (e) {
      toast.error(apiError(e))
    } finally {
      setExporting(false)
    }
  }
  return { filters, change, setPage, exporting, exportCsv }
}

// ── Nhật ký thao tác ─────────────────────────────────────────────────

const ACTIVITY_COLUMNS: Column<ActivityItem>[] = [
  {
    key: 'time', get header() { return i18n.t('settings:audit.activity.columns.time') }, className: 'whitespace-nowrap w-[170px]',
    cell: (r) => <span className="tabular-nums">{formatTime(r.createdAt)}</span>,
  },
  {
    key: 'actor', get header() { return i18n.t('settings:audit.activity.columns.actor') },
    cell: (r) => (
      <div className="min-w-0">
        <p className={r.actor ? 'truncate' : 'truncate text-muted-foreground'}>{r.actorLabel}</p>
        {r.actor?.email && <p className="truncate text-xs text-muted-foreground">{r.actor.email}</p>}
      </div>
    ),
  },
  { key: 'module', get header() { return i18n.t('settings:audit.activity.columns.module') }, cell: (r) => <Badge variant="secondary">{r.moduleLabel}</Badge> },
  { key: 'action', get header() { return i18n.t('settings:audit.activity.columns.action') }, cell: (r) => <span title={r.action}>{r.actionLabel}</span> },
  {
    key: 'target', get header() { return i18n.t('settings:audit.activity.columns.target') },
    cell: (r) => r.targetName ? <span className="break-words">{r.targetName}</span> : <span className="text-muted-foreground">—</span>,
  },
  {
    key: 'detail', get header() { return i18n.t('settings:audit.activity.columns.detail') }, className: 'max-w-[320px]',
    cell: (r) => r.detailText ? <span className="text-muted-foreground">{r.detailText}</span> : <span className="text-muted-foreground">—</span>,
  },
]

export function ActivityLogTab() {
  const { filters, change, setPage, exporting, exportCsv } = useFilters()
  const { data: modules } = useAuditModules()
  const { data, isLoading, isError, error } = useActivityLogs(filters)
  const { t } = useTranslation('settings')

  return (
    <div className="space-y-3">
      <FilterBar filters={filters} onChange={change} onExport={() => exportCsv('activity')} exporting={exporting}>
        <Select value={filters.module ?? 'all'} onValueChange={(v) => change({ module: v })}>
          <SelectTrigger className="w-[160px]" aria-label={t('settings:audit.activity.moduleLabel')}>
            <SelectValue placeholder={t('settings:audit.activity.allModules')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('settings:audit.activity.allModules')}</SelectItem>
            {(modules ?? []).map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </FilterBar>
      {isError ? (
        <ErrorState message={apiError(error)} />
      ) : (
        <>
          <DataTable
            columns={ACTIVITY_COLUMNS}
            rows={data?.items ?? []}
            loading={isLoading}
            rowKey={(r) => r.id}
            emptyTitle={t('settings:audit.activity.empty')}
          />
          {!!data?.total && (
            <Pagination page={data.page} limit={data.limit} total={data.total} onPageChange={setPage} />
          )}
        </>
      )}
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <History className="h-3.5 w-3.5" />
        {t('settings:audit.activity.footerHint')}
      </p>
    </div>
  )
}

// ── Nhật ký đăng nhập ────────────────────────────────────────────────

export const LOGIN_COLUMNS: Column<LoginItem>[] = [
  {
    key: 'type', get header() { return i18n.t('settings:audit.login.columns.type') },
    cell: (r) => (
      <div>
        <Badge variant={r.success ? 'success' : 'destructive'}>{r.typeLabel}</Badge>
        {!r.success && r.failureReason && (
          <p className="mt-1 text-xs text-muted-foreground">{FAILURE_LABELS[r.failureReason] ?? r.failureReason}</p>
        )}
      </div>
    ),
  },
  {
    key: 'user', get header() { return i18n.t('settings:audit.login.columns.user') },
    cell: (r) => (
      <div className="min-w-0">
        <p className="truncate">{r.user?.fullName ?? i18n.t('settings:audit.deletedMember')}</p>
        {r.user?.email && <p className="truncate text-xs text-muted-foreground">{r.user.email}</p>}
      </div>
    ),
  },
  {
    key: 'ip', header: 'IP',
    cell: (r) => (
      <div>
        <span className="tabular-nums">{r.ip ?? '—'}</span>
        {r.location && <span className="ml-1 text-xs text-muted-foreground">({r.location})</span>}
        {r.ipChanged && (
          <p className="mt-1 flex items-start gap-1 text-xs text-warning">
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
            {IP_CHANGED_WARNING()}
          </p>
        )}
      </div>
    ),
  },
  { key: 'device', get header() { return i18n.t('settings:audit.login.columns.device') }, cell: (r) => deviceText(r) },
  {
    key: 'time', get header() { return i18n.t('settings:audit.login.columns.time') }, className: 'whitespace-nowrap',
    cell: (r) => <span className="tabular-nums">{formatTime(r.createdAt)}</span>,
  },
]

export function LoginLogTab() {
  const { filters, change, setPage, exporting, exportCsv } = useFilters()
  const { data, isLoading, isError, error } = useLoginLogs(filters)
  const { t } = useTranslation('settings')

  return (
    <div className="space-y-3">
      <FilterBar filters={filters} onChange={change} onExport={() => exportCsv('logins')} exporting={exporting}>
        <Select
          value={filters.success || 'all'}
          onValueChange={(v) => change({ success: v === 'all' ? '' : (v as 'true' | 'false') })}
        >
          <SelectTrigger className="w-[190px]" aria-label={t('settings:audit.login.typeLabel')}>
            <SelectValue placeholder={t('common:misc.all')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('settings:audit.login.allLogins')}</SelectItem>
            <SelectItem value="true">{t('settings:audit.login.successOnly')}</SelectItem>
            <SelectItem value="false">{t('settings:audit.login.failedOnly')}</SelectItem>
          </SelectContent>
        </Select>
      </FilterBar>
      {isError ? (
        <ErrorState message={apiError(error)} />
      ) : (
        <>
          <DataTable
            columns={LOGIN_COLUMNS}
            rows={data?.items ?? []}
            loading={isLoading}
            rowKey={(r) => r.id}
            emptyTitle={t('settings:audit.login.empty')}
          />
          {!!data?.total && (
            <Pagination page={data.page} limit={data.limit} total={data.total} onPageChange={setPage} />
          )}
        </>
      )}
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <LogIn className="h-3.5 w-3.5" />
        {t('settings:audit.login.footerHint')}
      </p>
    </div>
  )
}

// ── Nhật ký trạng thái trực tuyến ────────────────────────────────────

export const PRESENCE_COLUMNS: Column<PresenceLogItem>[] = [
  {
    key: 'user', get header() { return i18n.t('settings:audit.presence.columns.user') },
    cell: (r) => (
      <div className="min-w-0">
        <p className="truncate">{r.user?.fullName ?? i18n.t('settings:audit.deletedMember')}</p>
        {r.user?.email && <p className="truncate text-xs text-muted-foreground">{r.user.email}</p>}
      </div>
    ),
  },
  {
    key: 'status', get header() { return i18n.t('settings:audit.presence.columns.status') },
    cell: (r) => (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
        <span className={cn('h-2 w-2 shrink-0 rounded-full', PRESENCE_DOT[r.status] ?? 'bg-muted-foreground/40')} />
        {r.statusLabel}
      </span>
    ),
  },
  { key: 'reason', get header() { return i18n.t('settings:audit.presence.columns.reason') }, cell: (r) => r.reasonLabel },
  {
    key: 'time', get header() { return i18n.t('settings:audit.presence.columns.updatedAt') }, className: 'whitespace-nowrap',
    cell: (r) => <span className="tabular-nums">{formatTime(r.startedAt)}</span>,
  },
  {
    key: 'duration', get header() { return i18n.t('settings:audit.presence.columns.duration') }, className: 'whitespace-nowrap',
    cell: (r) => (
      <span className={cn('tabular-nums', r.ongoing && 'text-primary')} title={r.ongoing ? i18n.t('settings:audit.presence.ongoingTitle') : undefined}>
        {r.durationLabel || '—'}
      </span>
    ),
  },
]

export function PresenceLogTab() {
  const { filters, change, setPage, exporting, exportCsv } = useFilters()
  const { data, isLoading, isError, error } = usePresenceLogs(filters)
  const { t } = useTranslation('settings')

  return (
    <div className="space-y-3">
      <FilterBar filters={filters} onChange={change} onExport={() => exportCsv('presence')} exporting={exporting}>
        <Select value={filters.status ?? 'all'} onValueChange={(v) => change({ status: v })}>
          <SelectTrigger className="w-[170px]" aria-label={t('settings:audit.presence.statusLabel')}>
            <SelectValue placeholder={t('settings:audit.presence.allStatuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('settings:audit.presence.allStatuses')}</SelectItem>
            {PRESENCE_OPTIONS.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </FilterBar>
      {isError ? (
        <ErrorState message={apiError(error)} />
      ) : (
        <>
          <DataTable
            columns={PRESENCE_COLUMNS}
            rows={data?.items ?? []}
            loading={isLoading}
            rowKey={(r) => r.id}
            emptyTitle={t('settings:audit.presence.empty')}
          />
          {!!data?.total && (
            <Pagination page={data.page} limit={data.limit} total={data.total} onPageChange={setPage} />
          )}
        </>
      )}
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <CircleDot className="h-3.5 w-3.5" />
        {t('settings:audit.presence.footerHint')}
      </p>
    </div>
  )
}
